/**
 * Glisser une ligne au doigt sur une longue offre : la page défile toute seule
 * quand le doigt approche du haut ou du bas de l'écran. Sans cela, une ligne
 * ne pouvait pas aller plus loin que ce que l'écran montre : impossible de
 * mettre la première tout en bas d'une offre de quinze lignes au téléphone.
 */

/** Part de l'écran, en haut et en bas, où le doigt fait défiler la page. */
const BORD = 0.2;
/** Défilement le plus rapide, en pixels par image, tout contre le bord. */
const VITESSE_MAX = 20;

/**
 * Le premier parent qui défile, sinon la page elle-même.
 *
 * @param {Element} el élément de départ
 * @returns {Element} ce qui défile
 */
export function parentQuiDefile(el) {
  let n = el && el.parentElement;
  while (n && n !== document.body && n !== document.documentElement) {
    const s = window.getComputedStyle(n);
    if (/(auto|scroll)/.test(s.overflowY) && n.scrollHeight > n.clientHeight) return n;
    n = n.parentElement;
  }
  return document.scrollingElement || document.documentElement;
}

/**
 * Vitesse de défilement pour un doigt à la hauteur {@code y} : vers le haut
 * (négative) près du haut de l'écran, vers le bas près du bas, nulle au milieu.
 *
 * @param {number} y hauteur du doigt dans l'écran
 * @param {number} hauteur hauteur de l'écran
 * @returns {number} pixels à défiler pour cette image
 */
export function vitesseAuBord(y, hauteur) {
  const bord = hauteur * BORD;
  if (bord <= 0) return 0;
  if (y < bord) return -Math.max(2, Math.round(VITESSE_MAX * (1 - Math.max(0, y) / bord)));
  if (y > hauteur - bord) {
    return Math.max(2, Math.round(VITESSE_MAX * (1 - Math.max(0, hauteur - y) / bord)));
  }
  return 0;
}

/**
 * Le défilement d'un glissé : {@code demarre} quand la ligne est prise,
 * {@code suit} à chaque mouvement du doigt, {@code arrete} quand on la lâche.
 * Après chaque pas de défilement, {@code quandDefile(y)} replace la ligne sous
 * le doigt, qui n'a pas bougé alors que la liste, elle, a défilé.
 *
 * @returns {{demarre: Function, suit: Function, arrete: Function}} le défilement
 */
export function creeDefilementAuBord() {
  let y = null;
  let cible = null;
  let quandDefile = null;
  let image = 0;
  // Pendant le glissé, le doigt déplace la ligne et non la page : Safari
  // faisait défiler l'offre en même temps, et la ligne partait avec.
  const empeche = (e) => {
    if (e.cancelable) e.preventDefault();
  };
  const pas = () => {
    image = 0;
    if (y == null || !cible) return;
    const dy = vitesseAuBord(y, window.innerHeight);
    if (dy) {
      const avant = cible.scrollTop;
      cible.scrollTop = avant + dy;
      if (cible.scrollTop !== avant && quandDefile) quandDefile(y);
    }
    image = window.requestAnimationFrame(pas);
  };
  return {
    demarre(el, surDefilement) {
      cible = parentQuiDefile(el);
      quandDefile = surDefilement;
      window.addEventListener('touchmove', empeche, { passive: false });
    },
    suit(nouveauY) {
      y = nouveauY;
      if (!image && cible) image = window.requestAnimationFrame(pas);
    },
    arrete() {
      y = null;
      if (image) window.cancelAnimationFrame(image);
      image = 0;
      cible = null;
      quandDefile = null;
      window.removeEventListener('touchmove', empeche);
    },
  };
}
